import { useCallback, useEffect, useMemo, useState } from "react";
import type { RajasthanGnaTgna } from "../../../models/rajasthan";
import { getGnaTgnaData } from "../Utility/RestApi";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import { FUNCTIONS } from "../const_raj";
import { R_CHART_TYPES } from "../const";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { convertToString, getMonthFromDate } from "../../../extensions/date";
import Tooltip from "../../../components/tooltip";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import SecondaryHeader from "../Demand/SecondaryHeader";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import { SearchBox } from "../../../components/Search";
import { BrushStart } from "../../../models/chart_model";
import { renderHourTick } from "../../../components/recharts/components";
import { stringToConsistentColor } from "../../../extensions/string";
import { AddDatesWithFileName } from "../../../helpers/helper";
const GROUP_BY = [
  {
    label: "From State",
    key: "from_state__name",
  },
  {
    label: "From Utility",
    key: "from_utility__name",
  },
  {
    label: "From Utility State",
    key: "from_utility__entity_state__name",
  },
  {
    label: "To State",
    key: "to_state__name",
  },
  {
    label: "To Utility",
    key: "to_utility__name",
  },
  {
    label: "IR Link",
    key: "ir_link__name",
  },
  {
    label: "Trader",
    key: "trader__name",
  },
  {
    label: "Category",
    key: "category__category",
  },
];

const KEYS = [
  {
    label: "Time Slot",
    key: "time",
    name: "Time Slot",
    firstKey: "time_block",
    secondKey: "date",
    values: ["time_block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    name: "Date",
    firstKey: "date",
    secondKey: undefined,
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    name: "Month",
    firstKey: "month",
    secondKey: undefined,
    values: ["month"],
    isTime: false,
  },
];

export default function RajasthanGnaTgna() {
  const [TableData, setTableData] = useState<any[]>([]);
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates
  );
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [data, setData] = useState<RajasthanGnaTgna[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState(KEYS[0]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[2]);
  const [selectedChartType, setSelectedChartType] = useState(R_CHART_TYPES[2]);
  const [loading, setLoading] = useState(true);
  const [selectedFunction, setSelectedFunction] = useState(FUNCTIONS[0]);

  const getKeys = useCallback(() => {
    if (selectedKey.key === "time") {
      return ["date", "time_block"];
    } else if (selectedKey.key === "date") {
      return ["date"];
    } else {
      return [selectedKey.values[0]];
    }
  }, [selectedKey]);

  useEffect(() => {
    let filteredData = data;
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.time_block)
      );
    }
    const par: ChartDataParams = {
      keys: getKeys(),
      tableKeys: ["date", "time_block"],
      groupBy: (selectedFunction.comparative
        ? [selectedGroupKeys.key]
        : []
      ).concat(["buy_sell"]),
      volumeKey: "value_mw",
      aggregationKeys: ["value_mwh"],
      priceKey: "",
      // averageKeys: ["value_mw"],
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setTableData(cData.tableData || []);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, selectedGroupKeys, selectedFunction, getKeys, selectedBlock]);

  const _handlefetchData = useCallback(async () => {
    if (DefaultDates[activeTab].defaultRangeDate === undefined) {
      return;
    }
    setLoading(true);
    const dates = DefaultDates[activeTab].defaultRangeDate;

    const response = await getGnaTgnaData({
      startDate: convertToString(dates.startDate),
      endDate: convertToString(dates.endDate),
    });
    const processedGnaTgnaData = response.map((item) => {
      const isBuy = item.value_mw > 0;
      return {
        ...item,
        type: `${item.category__category} ${isBuy ? "Buy" : "Sell"}`,
        buy_sell: isBuy ? "Buy" : "Sell",
        entity: item.from_utility__name,
        toEntity: item.to_utility__name,
        value_mwh: Math.abs(item.value_mw) / 4,
        value_mw: Math.abs(item.value_mw),
        month: getMonthFromDate(item.date),
      };
    });

    setData(processedGnaTgnaData);
    setUniqueBlock(
      Array.from(new Set(processedGnaTgnaData.map((item) => item.time_block)))
    );
    setLoading(false);
  }, [DefaultDates, activeTab]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const handlexDataKey = useCallback(() => {
    if (selectedKey.key === "time") {
      if (selectedBlock.length > 0) {
        return "time_block";
      }
      return "time_block";
    } else {
      // if (selectedUnit !== DEMAND_UNITS[1]) {
      //   setSelectedUnit(DEMAND_UNITS[1]);
      // }
      return selectedKey.key;
    }
  }, [selectedBlock.length, selectedKey.key]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  const summaryExtraKeys = selectedKey.isTime
    ? [
        "time_block", // Changed dateIndex to date
        selectedKey.values[1],
      ]
    : [selectedKey.values[0]];

  const summaryExtraLabels = selectedKey.values.map(
    (value) => (value === "date" ? "Date" : value) // Simplified label mapping
  );

  function getActiveLegends() {
    return chartKeys
      .sort(
        // sort by first reversing the string and then compared with another reversed string
        (a, b) =>
          b
            .split("_")
            .reverse()
            .join("")
            .localeCompare(a.split("_").reverse().join(""))
      )
      .reverse() // Reverse again to get the desired final order
      .map((key, index) => {
        return {
          dataKey: key + "_value_mw",
          name: key.replaceAll("_", " ").replaceAll("null", "Others"), // Clean up name
          legendColor: undefined,
          stroke: stringToConsistentColor(key),
          type: selectedChartType.value, // Set chart type (bar, line, etc.)
          stackId: key.includes("Buy") ? "buy" : "sell", // Group Buy/Sell for stacking
        };
      });
  }

  const tablePar = {
    legends: getActiveLegends(),
    data: chartData || [],
    tableData: TableData,
    itemsPerPage: 96,
    unit: "MW",
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Power Procurement Gna/Tgna",
      dates: DefaultDates[activeTab].defaultRangeDate,
    }),
  };

  function secondDataKey() {
    if (selectedKey.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const handleKeyChange = (index: number) => {
    setSelectedKey(KEYS[index]);
  };

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            items={KEYS.map((item) => item.name)}
            selectedItems={[selectedKey.name]}
            onSelect={(item, index) => {
              handleKeyChange(index);
            }}
            title={"Interval"}
          />
          <FilterMenu
            title="Function"
            items={FUNCTIONS.map((item) => item.label)}
            selectedItems={[selectedFunction.label]}
            onSelect={(item, index) => {
              setSelectedFunction(FUNCTIONS[index]);
            }}
          />
          <FilterMenu
            title="Group By"
            items={GROUP_BY.map((item) => item.label)}
            selectedItems={[selectedGroupKeys.label]}
            onSelect={(item, index) => {
              setSelectedGroupKeys(GROUP_BY[index]);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={R_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(R_CHART_TYPES[index]);
            }}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        <div className="gas-chart-container">
          {loading ? <ChartAreaLoader title="Loading GNA/TGNA" subtitle="Fetching GNA and TGNA drawal volume data…" /> : (<>
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">Volume Drawal (MW)</h2>
            <div className="flex gap-x-6  justify-end">
              <SearchBox
                options={uniqueBlock.map((item) => {
                  return { value: item, label: item };
                })}
                isMany={true}
                placeholder="Search Block"
                selectedOptions={selectedBlock}
                onChange={setSelectedBlock}
              />
              <button
                onClick={() => {
                  const secureTablePar = {
                    ...tablePar,
                    data: chartData || [],
                  };
                  dataTab.label === "Chart" &&
                    downloadChartTableCSV(secureTablePar);
                  dataTab.label === "Summary" &&
                    downloadSummaryCSV(secureTablePar);
                  dataTab.label === "Table" &&
                    downloadChartTableCSV(secureTablePar);
                }}
                className="downloadButton"
              >
                Download Data
              </button>
            </div>
          </div>
          {dataTab.label === "Chart" && (
            <ReMixChart
              data={chartData}
              xDataKey={xDataKey}
              secondXDataKey={secondDataKey()}
              unit="MW"
              isTimeSlot={selectedKey.isTime}
              showBrush={chartData.length > 96 ? true : undefined}
              brushStart={BrushStart.Start}
              onlyTitle={true}
              brushIndex={{
                startIndex: 0,
                endIndex: 96 * 1,
              }}
              legends={getActiveLegends()}
              xTick={
                selectedKey.isTime && selectedBlock.length == 0
                  ? (value: any, index: any) => {
                      return renderHourTick(value, 10);
                    }
                  : undefined
              }
            />
          )}
          {dataTab.label === "Summary" && <LegendSummaryTable {...tablePar} />}
          {dataTab.label === "Table" && <ChartTable {...tablePar} />}
          </>)}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<div>WBES</div>}
              text="
                1. WBES: OA GNA reports, OA TGNA reports
                2. MW: Represents the average (MW) when the selected time interval is Date.
"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
